/**
 * Query plumbing for the learner side of the training redesign — My learning, My team and the Home
 * card. Kept apart from the page components the same way `features/training/hooks.ts` is.
 */

import { useQueryClient } from '@tanstack/react-query';
import { apiErrorStatus } from '../../lib/api-error.js';
import { tsr } from '../../lib/api.js';
import { trainingKeys } from '../../routes/training/hooks.js';

export const learnerKeys = {
  home: () => ['training', 'learner', 'home'] as const,
  team: (q: string, overdueOnly: boolean) => ['training', 'learner', 'team', q, overdueOnly] as const,
};

/** A route that answers 503 is still being built (redesign rollout): retrying it only delays the
 *  friendly "coming soon" state; anything else gets the app's usual retry. */
export function retryUnlessUnavailable(failureCount: number, error: unknown): boolean {
  const status = apiErrorStatus(error);
  if (status === 503) return false;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < 2;
}

export function isUnavailable(error: unknown): boolean {
  return apiErrorStatus(error) === 503;
}

/** My learning's one read: courses, the one to continue, older items, certificate count. Shared by
 *  the page and the Home card through the same key, so Home costs nothing extra after a visit. */
export function useLearnerHome(options: { enabled?: boolean } = {}) {
  return tsr.trainingLearner.home.useQuery({
    queryKey: learnerKeys.home(),
    enabled: options.enabled ?? true,
    retry: retryUnlessUnavailable,
  });
}

export function useLearnerTeam(q: string, overdueOnly: boolean, enabled: boolean) {
  return tsr.trainingLearner.team.useQuery({
    queryKey: learnerKeys.team(q, overdueOnly),
    queryData: { query: { limit: 100, q: q || undefined, overdueOnly: overdueOnly || undefined } },
    enabled,
    retry: retryUnlessUnavailable,
  });
}

/** After anything that changes a learner's progress: My learning, Home and the old `today` feed. */
export function useInvalidateLearnerHome() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: learnerKeys.home() });
    void queryClient.invalidateQueries({ queryKey: trainingKeys.today() });
  };
}
